import { httpClient } from "../http/client";

const EXPORTER_GET_BASE_PATH = "/admin/exporter/get";
const EXPORTER_EXPORT_BASE_PATH = "/admin/exporter/export";
const EXPORTER_EXPORT_MULTIPLE_BASE_PATH = "/admin/exporter/exportMultiple";

export interface ExporterQueryAttributeDefinition {
  name: string;
  description: string;
  clazz: string;
}

export interface ExporterQueryDefinition {
  queryType: string;
  description: string;
  queryAttrDefinition: ExporterQueryAttributeDefinition[];
}

export interface ExporterDefinition {
  writtenBy: string;
  category: string;
  name: string;
  description: string;
  shortName: string;
  supportedRoles: string[];
  supportedDomains: string[];
  authorized: boolean;
  queryDefinition: ExporterQueryDefinition[];
  exportAttrDefinition: string[];
}

export type ExporterQueryParams = Record<string, string>;

export interface ExporterCsvTable {
  headers: string[];
  rows: string[][];
}

export type ExporterPayloadFormat = "csv" | "json";

export interface ExporterExecuteResult {
  format: ExporterPayloadFormat;
  /** Present when `format` is `"csv"`. */
  csvText: string;
  /** Present when `format` is `"json"` — parsed JSON payload (array or object). */
  jsonData: unknown | null;
  fileName: string | null;
  contentType: string | null;
  status: number;
  requestUrl: string;
  requestBody: ExporterQueryParams;
}

export interface ExporterExecuteRequest {
  exporter: string;
  queryType: string;
  queryParams?: ExporterQueryParams;
}

export interface ExporterExecuteMultipleRequest extends ExporterExecuteRequest {
  pageNumber: number;
  pageSize: number;
  /** Override Authorization (e.g. Spot Viewer Basic). */
  authorization?: string;
}

/**
 * Fetches the exporter definition for a named admin exporter.
 * GET /admin/exporter/get/{exporterName}
 *
 * Uses the shared `httpClient` (base URL from localStorage ?? configuredBaseURL, Basic/Bearer auth interceptors).
 */
export async function fetchExporterDefinition(
  exporterName: string,
): Promise<ExporterDefinition> {
  const trimmedName = exporterName.trim();
  if (!trimmedName) {
    throw new Error("Exporter name is required.");
  }

  const response = await httpClient.get<unknown>(
    `${EXPORTER_GET_BASE_PATH}/${encodeURIComponent(trimmedName)}`,
    {
      headers: {
        Accept: "application/json",
      },
    },
  );

  return parseExporterDefinition(response.data);
}

/**
 * @deprecated Prefer `fetchExporterDefinition`. Kept for callers that expect the raw GET payload.
 */
export async function fetchExporterData(exporterName: string): Promise<unknown> {
  return fetchExporterDefinition(exporterName);
}

/**
 * Executes a named exporter query and returns CSV text or parsed JSON export data.
 * POST /admin/exporter/export/{exporterName}/{queryType}
 *
 * Uses an empty JSON body `{}` when the selected query has no parameters.
 * Disables Axios JSON auto-parse so CSV responses are not mishandled when Content-Type varies.
 */
export async function executeExporter(
  request: ExporterExecuteRequest,
): Promise<ExporterExecuteResult> {
  const exporter = request.exporter.trim();
  const queryType = request.queryType.trim();
  if (!exporter) {
    throw new Error("Exporter name is required.");
  }
  if (!queryType) {
    throw new Error("Exporter query type is required.");
  }

  const url = `${EXPORTER_EXPORT_BASE_PATH}/${encodeURIComponent(exporter)}/${encodeURIComponent(queryType)}`;
  const queryParams = request.queryParams ?? {};

  return postExporterExport(url, queryParams);
}

/**
 * Executes a named exporter query with pagination and returns CSV text or parsed JSON export data.
 * POST /admin/exporter/exportMultiple/{exporterName}/{queryType}/{pageNumber}/{pageSize}
 *
 * Uses an empty JSON body `{}` when the selected query has no parameters.
 */
export async function executeExporterMultiple(
  request: ExporterExecuteMultipleRequest,
): Promise<ExporterExecuteResult> {
  const exporter = request.exporter.trim();
  const queryType = request.queryType.trim();
  if (!exporter) {
    throw new Error("Exporter name is required.");
  }
  if (!queryType) {
    throw new Error("Exporter query type is required.");
  }
  if (!Number.isFinite(request.pageNumber) || request.pageNumber < 1) {
    throw new Error("Exporter page number must be a positive integer.");
  }
  if (!Number.isFinite(request.pageSize) || request.pageSize < 1) {
    throw new Error("Exporter page size must be a positive integer.");
  }

  const pageNumber = Math.trunc(request.pageNumber);
  const pageSize = Math.trunc(request.pageSize);
  const url =
    `${EXPORTER_EXPORT_MULTIPLE_BASE_PATH}/`
    + `${encodeURIComponent(exporter)}/`
    + `${encodeURIComponent(queryType)}/`
    + `${pageNumber}/${pageSize}`;
  const queryParams = request.queryParams ?? {};

  return postExporterExport(url, queryParams, {
    authorization: request.authorization,
  });
}

async function postExporterExport(
  url: string,
  queryParams: ExporterQueryParams,
  options?: { authorization?: string },
): Promise<ExporterExecuteResult> {
  logExporterDebug("request", {
    url,
    method: "POST",
    body: queryParams,
  });

  const response = await httpClient.post<unknown>(url, queryParams, {
    // Keep the body as raw text so CSV is preserved even if Content-Type is wrong/missing.
    responseType: "text",
    transformResponse: [(data) => data],
    headers: {
      Accept: "text/csv, application/json;q=0.9, */*;q=0.8",
      "Content-Type": "application/json",
      ...(options?.authorization ? { Authorization: options.authorization } : {}),
    },
  });

  const contentType = readResponseHeader(response.headers, "content-type");
  const contentDisposition = readResponseHeader(response.headers, "content-disposition");
  const fileName = extractFilenameFromContentDisposition(contentDisposition);
  const status = response.status;
  const rawBody = normalizeExportResponseBody(response.data);
  const preview = previewResponseBody(rawBody);

  logExporterDebug("response", {
    url,
    method: "POST",
    body: queryParams,
    status,
    contentType,
    preview,
  });

  const looksLikeDefinition = isExporterDefinitionPayload(rawBody);
  if (looksLikeDefinition) {
    throw new Error(
      `Exporter export returned an exporter definition instead of export data. `
        + `Check that POST ${url} is used (not GET /admin/exporter/get/...). `
        + `Content-Type: ${contentType ?? "unknown"}.`,
    );
  }

  const format = detectExporterPayloadFormat(contentType, rawBody);
  if (format === "json") {
    const jsonData = parseExportJsonBody(rawBody);
    if (isExporterDefinitionPayload(jsonData)) {
      throw new Error(
        `Exporter export returned an exporter definition JSON payload. `
          + `Expected CSV/JSON export rows from POST ${url}.`,
      );
    }

    return {
      format: "json",
      csvText: "",
      jsonData,
      fileName,
      contentType,
      status,
      requestUrl: url,
      requestBody: queryParams,
    };
  }

  const csvText = typeof rawBody === "string" ? rawBody : "";
  return {
    format: "csv",
    csvText,
    jsonData: null,
    fileName,
    contentType,
    status,
    requestUrl: url,
    requestBody: queryParams,
  };
}

/**
 * Picks a query type from the exporter definition.
 * Prefers "All" when listed; otherwise uses the first query returned by the backend.
 */
export function resolveExporterQueryType(
  definition: ExporterDefinition,
  preferredQueryType: string = "All",
): ExporterQueryDefinition {
  const queries = definition.queryDefinition ?? [];
  if (queries.length === 0) {
    throw new Error(`Exporter "${definition.name}" has no available queries.`);
  }

  const preferred = preferredQueryType.trim().toLowerCase();
  const match = preferred
    ? queries.find((query) => query.queryType.trim().toLowerCase() === preferred)
    : undefined;

  return match ?? queries[0];
}

/**
 * Builds query params for an exporter query.
 * Returns `{}` when the query has no attributes (backend expects an empty JSON body).
 * Attributes default to empty string when not supplied via overrides.
 */
export function buildExporterQueryParams(
  queryDefinition: ExporterQueryDefinition,
  overrides: ExporterQueryParams = {},
): ExporterQueryParams {
  const params: ExporterQueryParams = {};
  for (const attribute of queryDefinition.queryAttrDefinition ?? []) {
    const name = attribute.name?.trim();
    if (!name) {
      continue;
    }
    params[name] = overrides[name] ?? "";
  }

  for (const [key, value] of Object.entries(overrides)) {
    if (!(key in params)) {
      params[key] = value;
    }
  }

  return params;
}

/**
 * Parses exporter CSV text into headers + rows.
 */
export function parseExporterCsv(csvText: string): ExporterCsvTable {
  const normalized = csvText.replace(/^\uFEFF/, "");
  if (!normalized.trim()) {
    return { headers: [], rows: [] };
  }

  const records: string[][] = [];
  let currentValue = "";
  let currentRow: string[] = [];
  let isInsideQuotes = false;

  function pushValue() {
    currentRow.push(currentValue);
    currentValue = "";
  }

  function pushRow() {
    records.push(currentRow);
    currentRow = [];
  }

  for (let index = 0; index < normalized.length; index += 1) {
    const char = normalized[index];
    const nextChar = normalized[index + 1];

    if (char === '"') {
      if (isInsideQuotes && nextChar === '"') {
        currentValue += '"';
        index += 1;
      } else {
        isInsideQuotes = !isInsideQuotes;
      }
      continue;
    }

    if (!isInsideQuotes && char === ",") {
      pushValue();
      continue;
    }

    if (!isInsideQuotes && (char === "\n" || char === "\r")) {
      if (char === "\r" && nextChar === "\n") {
        index += 1;
      }
      pushValue();
      pushRow();
      continue;
    }

    currentValue += char;
  }

  pushValue();
  if (currentRow.length > 1 || currentRow[0] !== "" || records.length === 0) {
    pushRow();
  }

  if (records.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = records[0];
  const rows = records.slice(1).filter((row) => row.some((value) => value.trim() !== ""));
  const normalizedRows = rows.map((row) => {
    if (row.length === headers.length) {
      return row;
    }

    if (row.length < headers.length) {
      return [...row, ...Array.from({ length: headers.length - row.length }, () => "")];
    }

    return row.slice(0, headers.length);
  });

  return {
    headers,
    rows: normalizedRows,
  };
}

/**
 * Converts a parsed CSV table into row objects keyed by header name.
 */
export function exporterCsvToRecords(table: ExporterCsvTable): Record<string, string>[] {
  const { headers, rows } = table;
  if (headers.length === 0) {
    return [];
  }

  return rows.map((row) => {
    const record: Record<string, string> = {};
    headers.forEach((header, index) => {
      const key = header.trim();
      if (!key) {
        return;
      }
      record[key] = row[index] ?? "";
    });
    return record;
  });
}

function parseExporterDefinition(payload: unknown): ExporterDefinition {
  if (!payload || typeof payload !== "object") {
    throw new Error("Invalid exporter definition response.");
  }

  const item = payload as Record<string, unknown>;
  const queryDefinition = Array.isArray(item.queryDefinition)
    ? item.queryDefinition
        .filter(isQueryDefinition)
        .map((query) => ({
          queryType: query.queryType,
          description: typeof query.description === "string" ? query.description : "",
          queryAttrDefinition: Array.isArray(query.queryAttrDefinition)
            ? query.queryAttrDefinition.filter(isQueryAttributeDefinition).map((attr) => ({
                name: attr.name,
                description: typeof attr.description === "string" ? attr.description : "",
                clazz: typeof attr.clazz === "string" ? attr.clazz : "",
              }))
            : [],
        }))
    : [];

  const name = typeof item.name === "string" ? item.name : "";
  if (!name.trim()) {
    throw new Error("Exporter definition is missing a name.");
  }

  return {
    writtenBy: typeof item.writtenBy === "string" ? item.writtenBy : "",
    category: typeof item.category === "string" ? item.category : "",
    name,
    description: typeof item.description === "string" ? item.description : "",
    shortName: typeof item.shortName === "string" ? item.shortName : name,
    supportedRoles: Array.isArray(item.supportedRoles)
      ? item.supportedRoles.filter((role): role is string => typeof role === "string")
      : [],
    supportedDomains: Array.isArray(item.supportedDomains)
      ? item.supportedDomains.filter((domain): domain is string => typeof domain === "string")
      : [],
    authorized: typeof item.authorized === "boolean" ? item.authorized : true,
    queryDefinition,
    exportAttrDefinition: Array.isArray(item.exportAttrDefinition)
      ? item.exportAttrDefinition.filter((attr): attr is string => typeof attr === "string")
      : [],
  };
}

function isQueryAttributeDefinition(
  value: unknown,
): value is ExporterQueryAttributeDefinition {
  if (!value || typeof value !== "object") {
    return false;
  }
  const item = value as Record<string, unknown>;
  return typeof item.name === "string";
}

function isQueryDefinition(value: unknown): value is ExporterQueryDefinition {
  if (!value || typeof value !== "object") {
    return false;
  }
  const item = value as Record<string, unknown>;
  return typeof item.queryType === "string";
}

function normalizeExportResponseBody(data: unknown): string | unknown {
  if (typeof data === "string" || data == null) {
    return data ?? "";
  }

  // Axios (or a proxy) may still surface an already-parsed object.
  return data;
}

function detectExporterPayloadFormat(
  contentType: string | null,
  body: unknown,
): ExporterPayloadFormat {
  const normalizedType = (contentType ?? "").toLowerCase();
  if (normalizedType.includes("csv") || normalizedType.includes("text/plain")) {
    return "csv";
  }
  if (normalizedType.includes("json") || normalizedType.includes("javascript")) {
    return "json";
  }

  if (typeof body !== "string") {
    return "json";
  }

  const trimmed = body.trim();
  if (!trimmed) {
    return "csv";
  }

  if (
    (trimmed.startsWith("{") && trimmed.endsWith("}"))
    || (trimmed.startsWith("[") && trimmed.endsWith("]"))
  ) {
    return "json";
  }

  return "csv";
}

function parseExportJsonBody(body: unknown): unknown {
  if (typeof body !== "string") {
    return body;
  }

  const trimmed = body.trim();
  if (!trimmed) {
    return null;
  }

  try {
    return JSON.parse(trimmed) as unknown;
  } catch {
    throw new Error("Exporter returned JSON Content-Type but the body could not be parsed as JSON.");
  }
}

function isExporterDefinitionPayload(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const item = value as Record<string, unknown>;
  return Array.isArray(item.queryDefinition) && Array.isArray(item.exportAttrDefinition);
}

function previewResponseBody(body: unknown, maxLines: number = 5): string {
  let text: string;
  if (typeof body === "string") {
    text = body;
  } else {
    try {
      text = JSON.stringify(body);
    } catch {
      text = String(body);
    }
  }

  return text
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .slice(0, maxLines)
    .join("\n");
}

function logExporterDebug(
  phase: "request" | "response",
  details: {
    url: string;
    method: string;
    body: ExporterQueryParams;
    status?: number;
    contentType?: string | null;
    preview?: string;
  },
): void {
  // Temporary debug logs for exporter integration verification.
  if (phase === "request") {
    console.info("[Exporter][debug] request", {
      url: details.url,
      method: details.method,
      body: details.body,
    });
    return;
  }

  console.info("[Exporter][debug] response", {
    url: details.url,
    method: details.method,
    body: details.body,
    status: details.status,
    contentType: details.contentType,
    preview: details.preview,
  });
}

function readResponseHeader(headers: unknown, headerName: string): string | null {
  if (!headers) {
    return null;
  }

  const normalizedName = headerName.toLowerCase();
  if (typeof (headers as { get?: (name: string) => unknown }).get === "function") {
    const value = (headers as { get: (name: string) => unknown }).get(headerName);
    return typeof value === "string" && value.trim() ? value : null;
  }

  if (typeof headers !== "object") {
    return null;
  }

  const record = headers as Record<string, unknown>;
  for (const [key, value] of Object.entries(record)) {
    if (key.toLowerCase() === normalizedName && typeof value === "string" && value.trim()) {
      return value;
    }
  }

  return null;
}

function extractFilenameFromContentDisposition(headerValue: string | null): string | null {
  if (!headerValue) {
    return null;
  }

  const utf8Match = /filename\*=UTF-8''([^;]+)/i.exec(headerValue);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1].trim().replace(/^"|"$/g, ""));
    } catch {
      return utf8Match[1].trim().replace(/^"|"$/g, "");
    }
  }

  const plainMatch = /filename=([^;]+)/i.exec(headerValue);
  if (plainMatch?.[1]) {
    return plainMatch[1].trim().replace(/^"|"$/g, "");
  }

  return null;
}
